/** @module @liquidefy/code-editor/themes/adaptive/focus @version 1.0.0 @license LQD Research LLC */

/**
 * Focus Mode Theme Adaptation — when focus/zen mode activates,
 * the theme shifts to reduce distraction.
 *
 * Chrome (sidebar, panels) is muted and desaturated.
 * Editor surfaces become slightly more opaque for readability.
 * Accent colors dim in non-editor areas.
 *
 * Pure functions. No DOM, no side effects.
 *
 * @rust focus.rs — false; 4/2/26; sm
 */

// ═══ §1 IMPORTS ═══

import {
  hexToOKLCH,
  oklchToHex,
  desaturate,
  rotateHue,
} from '../color/oklch.js';
import type { OKLCH } from '../color/oklch.js';

// ═══ §2 TYPES ═══

/** How strongly focus mode affects the theme */
export type FocusIntensity = 'subtle' | 'moderate' | 'immersive';

/**
 * The numeric shift parameters applied during focus mode.
 * Multipliers < 1 reduce, > 1 amplify.
 */
export interface FocusThemeShift {
  readonly intensity: FocusIntensity;
  readonly chromeOpacityMultiplier: number;
  readonly chromeSaturationMultiplier: number;
  readonly editorOpacityMultiplier: number;
  readonly accentDimming: number;
  readonly warmthShift: number;
}

/**
 * Tracks focus mode state, including original colors for restoration.
 */
export interface FocusThemeState {
  readonly isActive: boolean;
  readonly intensity: FocusIntensity;
  readonly shift: FocusThemeShift;
  readonly originalColors: Readonly<Record<string, string>> | null;
}

// ═══ §3 SHIFT PRESETS ═══

/** Shift parameters for each intensity level */
const SHIFT_PRESETS: Readonly<Record<FocusIntensity, FocusThemeShift>> = {
  subtle: {
    intensity: 'subtle',
    chromeOpacityMultiplier: 0.8,
    chromeSaturationMultiplier: 0.85,
    editorOpacityMultiplier: 1.1,
    accentDimming: 0.2,
    warmthShift: 0.0,
  },
  moderate: {
    intensity: 'moderate',
    chromeOpacityMultiplier: 0.6,
    chromeSaturationMultiplier: 0.65,
    editorOpacityMultiplier: 1.2,
    accentDimming: 0.4,
    warmthShift: 0.05,
  },
  immersive: {
    intensity: 'immersive',
    chromeOpacityMultiplier: 0.4,
    chromeSaturationMultiplier: 0.4,
    editorOpacityMultiplier: 1.3,
    accentDimming: 0.7,
    warmthShift: 0.1,
  },
};

// ═══ §4 STATE CREATION ═══

/**
 * Create the initial focus theme state (inactive).
 */
export function createFocusThemeState(): FocusThemeState {
  return {
    isActive: false,
    intensity: 'subtle',
    shift: SHIFT_PRESETS.subtle,
    originalColors: null,
  };
}

// ═══ §5 SHIFT ACCESSORS ═══

/**
 * Get the preset shift parameters for a given intensity.
 */
export function getShiftForIntensity(intensity: FocusIntensity): FocusThemeShift {
  return SHIFT_PRESETS[intensity];
}

// ═══ §6 FOCUS MODE LIFECYCLE ═══

/**
 * Enter focus mode. Stores the original theme colors and activates the shift.
 *
 * @param state      - Current focus state
 * @param themeColors - Current theme color map (to be saved for restoration)
 * @param intensity   - Desired focus intensity
 * @returns Updated state with originals saved and shift activated
 */
export function enterFocusMode(
  state: FocusThemeState,
  themeColors: Record<string, string>,
  intensity: FocusIntensity,
): FocusThemeState {
  return {
    isActive: true,
    intensity,
    shift: SHIFT_PRESETS[intensity],
    originalColors: { ...themeColors },
  };
}

/**
 * Exit focus mode. Restores the original colors reference.
 *
 * @param state - Current focus state
 * @returns Reset state (inactive, originals cleared)
 */
export function exitFocusMode(state: FocusThemeState): FocusThemeState {
  return {
    isActive: false,
    intensity: state.intensity,
    shift: state.shift,
    originalColors: null,
  };
}

/**
 * Change the intensity while focus mode is active.
 * If focus mode is not active, just updates the intensity preset.
 */
export function setFocusIntensity(
  state: FocusThemeState,
  intensity: FocusIntensity,
): FocusThemeState {
  return {
    ...state,
    intensity,
    shift: SHIFT_PRESETS[intensity],
  };
}

// ═══ §7 INTERNAL HELPERS ═══

/** Clamp to [min, max] */
function clamp(x: number, min: number, max: number): number {
  return x < min ? min : x > max ? max : x;
}

/** Check if a string is a valid hex color */
function isHex(s: string): boolean {
  return /^#[0-9a-fA-F]{3,8}$/.test(s);
}

/**
 * Known chrome color keys (sidebar, panel, activity bar, tabs, status bar).
 * These are the colors that get muted during focus mode.
 */
const CHROME_KEYS = new Set([
  'sideBar.background',
  'sideBar.foreground',
  'sideBar.border',
  'sideBarSectionHeader.background',
  'sideBarSectionHeader.foreground',
  'activityBar.background',
  'activityBar.foreground',
  'activityBar.border',
  'tab.inactiveBackground',
  'tab.inactiveForeground',
  'tab.border',
  'titleBar.activeBackground',
  'titleBar.activeForeground',
  'titleBar.inactiveBackground',
  'titleBar.inactiveForeground',
  'statusBar.background',
  'statusBar.foreground',
  'statusBar.border',
  'panel.background',
  'panel.border',
  'panelTitle.activeForeground',
  'panelTitle.inactiveForeground',
]);

/**
 * Known editor color keys. These are the colors that get slightly boosted
 * during focus mode for readability.
 */
const EDITOR_KEYS = new Set([
  'editor.background',
  'editor.foreground',
  'editor.lineHighlightBackground',
  'editorGutter.background',
]);

/**
 * Accent-related keys that get dimmed outside the editor area.
 */
const ACCENT_KEYS = new Set([
  'activityBarBadge.background',
  'focusBorder',
  'scrollbarSlider.activeBackground',
]);

// ═══ §8 COLOR ADJUSTMENT ═══

/**
 * Apply the focus shift to a complete theme color map.
 *
 * - Chrome colors: desaturated and opacity-reduced
 * - Editor colors: slightly boosted opacity/warmth
 * - Accent colors: dimmed in non-editor areas
 *
 * @param colors - Theme color map
 * @param shift  - Focus shift parameters
 * @returns Adjusted color map
 */
export function applyFocusShift(
  colors: Record<string, string>,
  shift: FocusThemeShift,
): Record<string, string> {
  const result: Record<string, string> = {};
  const keys = Object.keys(colors);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]!;
    const val = colors[key]!;

    if (!isHex(val)) {
      result[key] = val;
      continue;
    }

    const oklch = hexToOKLCH(val);

    if (CHROME_KEYS.has(key)) {
      // Mute chrome: reduce chroma, pull lightness toward neutral
      const newC = clamp(oklch.C * shift.chromeSaturationMultiplier, 0, 0.5);
      const newL = clamp(oklch.L * shift.chromeOpacityMultiplier, 0, 1);
      result[key] = oklchToHex({ L: newL, C: newC, H: oklch.H });
    } else if (EDITOR_KEYS.has(key)) {
      // Boost editor: slight warmth shift, slight opacity boost
      const warmHueShift = shift.warmthShift * 30; // Convert to degrees
      const newH = ((oklch.H + warmHueShift) % 360 + 360) % 360;
      result[key] = oklchToHex({ L: oklch.L, C: oklch.C, H: newH });
    } else if (ACCENT_KEYS.has(key)) {
      // Dim accent in non-editor areas
      const newL = clamp(oklch.L * (1 - shift.accentDimming * 0.3), 0, 1);
      const newC = clamp(oklch.C * (1 - shift.accentDimming * 0.4), 0, 0.5);
      result[key] = oklchToHex({ L: newL, C: newC, H: oklch.H });
    } else {
      result[key] = val;
    }
  }

  return result;
}

// ═══ §9 GLASS SURFACE ADJUSTMENT ═══

/**
 * Apply the focus shift to glass surface definitions.
 *
 * Glass surfaces that belong to chrome (sidebar, panels) have their
 * opacity reduced for a more transparent, faded appearance.
 * Editor glass surfaces get slightly more opaque for readability.
 *
 * @param surfaces - Map of surface name → {blur, opacity}
 * @param shift    - Focus shift parameters
 * @returns Adjusted surface map
 */
export function applyFocusToGlassSurfaces(
  surfaces: Record<string, { blur: number; opacity: number }>,
  shift: FocusThemeShift,
): Record<string, { blur: number; opacity: number }> {
  const result: Record<string, { blur: number; opacity: number }> = {};
  const keys = Object.keys(surfaces);

  // Heuristic: surface names containing 'sidebar', 'panel', 'activity',
  // 'status', 'tab' are chrome. 'editor' is editor.
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]!;
    const surface = surfaces[key]!;
    const lower = key.toLowerCase();

    const isChromeSurface =
      lower.includes('sidebar') ||
      lower.includes('panel') ||
      lower.includes('activity') ||
      lower.includes('status') ||
      lower.includes('tab') ||
      lower.includes('title');

    const isEditorSurface = lower.includes('editor');

    if (isChromeSurface) {
      result[key] = {
        blur: surface.blur,
        opacity: clamp(surface.opacity * shift.chromeOpacityMultiplier, 0, 1),
      };
    } else if (isEditorSurface) {
      result[key] = {
        blur: surface.blur,
        opacity: clamp(surface.opacity * shift.editorOpacityMultiplier, 0, 1),
      };
    } else {
      result[key] = { ...surface };
    }
  }

  return result;
}

// ═══ §10 SUMMARY ═══

/**
 * Get a display-friendly summary of the current focus shift parameters.
 * Returns all numeric values rounded to 2 decimal places.
 */
export function getFocusShiftSummary(shift: FocusThemeShift): Record<string, number> {
  return {
    chromeOpacity: Math.round(shift.chromeOpacityMultiplier * 100) / 100,
    chromeSaturation: Math.round(shift.chromeSaturationMultiplier * 100) / 100,
    editorOpacity: Math.round(shift.editorOpacityMultiplier * 100) / 100,
    accentDimming: Math.round(shift.accentDimming * 100) / 100,
    warmthShift: Math.round(shift.warmthShift * 100) / 100,
  };
}
